/**
 * The single persisted top-level sector for companies and investors.
 *
 * `primary_sector` used to call Health "Med Tech". That legacy value is
 * normalised on read so historic profiles remain valid while all new writes
 * use the clear Health / Climate labels.
 */
export type PrimarySector = "Health" | "Climate";

export const PRIMARY_SECTORS: readonly PrimarySector[] = [
  "Health",
  "Climate",
] as const;

export const DEFAULT_PRIMARY_SECTOR: PrimarySector = "Health";

export type SectorValue = Lowercase<PrimarySector>;

export const primarySectorSlug = (sector: PrimarySector): SectorValue =>
  sector.toLowerCase() as SectorValue;

export const isPrimarySector = (value: unknown): value is PrimarySector =>
  typeof value === "string" &&
  (PRIMARY_SECTORS as readonly string[]).includes(value);

export const parsePrimarySector = (
  value: unknown,
  fallback: PrimarySector = DEFAULT_PRIMARY_SECTOR,
): PrimarySector => {
  const candidate = Array.isArray(value) ? value[0] : value;
  if (typeof candidate !== "string") return fallback;

  const normalised = candidate.trim().toLowerCase();
  if (normalised === "med tech" || normalised === "health") return "Health";
  return (
    PRIMARY_SECTORS.find(
      (sector) => primarySectorSlug(sector) === normalised,
    ) ?? fallback
  );
};

export const primarySectorSelectOptions = PRIMARY_SECTORS.map((sector) => ({
  value: sector,
  label: sector,
}));

export const stripInvalidPrimarySector = (
  fields:
    | { primary_sector?: unknown; investment_primary_sector?: unknown }
    | undefined,
  key: "primary_sector" | "investment_primary_sector",
) => {
  if (!fields || !Object.hasOwn(fields, key) || isPrimarySector(fields[key]))
    return;
  delete fields[key];
};

/**
 * The Health / Climate sector question asked on the public sign-up and contact
 * forms. Derived from `PRIMARY_SECTORS` rather than hardcoded again, so there
 * is one top-level taxonomy list that cannot drift apart.
 */
export const SECTOR_OPTIONS: ReadonlyArray<{
  value: SectorValue;
  label: PrimarySector;
}> = PRIMARY_SECTORS.map((sector) => ({
  value: primarySectorSlug(sector),
  label: sector,
}));

/**
 * Prompt shown by the public sector control.
 */
export const SECTOR_PROMPT = "Sector";

/** Message shown when a public form is submitted without a sector. */
export const SECTOR_REQUIRED_ERROR = "Please select Health or Climate.";

export const isSectorValue = (value: unknown): value is SectorValue =>
  SECTOR_OPTIONS.some((option) => option.value === value);

/**
 * Narrows an untrusted posted value to a known sector, or undefined. The
 * question is optional, so this keeps unrecognised values out of the admin
 * email rather than gating the submission on them.
 */
export const parseSector = (value: unknown): SectorValue | undefined =>
  isSectorValue(value) ? value : undefined;

/** Human-readable sector name for admin emails. */
export const sectorLabel = (value: unknown): string =>
  SECTOR_OPTIONS.find((option) => option.value === value)?.label ??
  "Not specified";

/**
 * The primary sector a validated public answer corresponds to, or undefined
 * if the value is not recognised.
 */
export const sectorToPrimarySector = (
  value: unknown,
): PrimarySector | undefined =>
  SECTOR_OPTIONS.find((option) => option.value === value)?.label;
